import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import type { Book, EditionType } from '../../../shared/types/index.js';
import { useAuthStore } from '../../auth/store/useAuthStore.js';
import { api } from '../../../shared/api/api.js';
import {
  CATEGORIES,
  formSchema,
  type BookFormData,
} from './book-form/bookFormConstants.js';
import { BookFormHeader } from './book-form/BookFormHeader.js';
import { DraftRestoreBanner } from './book-form/DraftRestoreBanner.js';
import { UibnPreviewCard } from './book-form/UibnPreviewCard.js';
import { BookMetadataFields } from './book-form/BookMetadataFields.js';
import { BookFormFooter } from './book-form/BookFormFooter.js';
import { UibnSubmissionFlowGuideModal } from './UibnSubmissionFlowGuideModal.js';
import { PublisherAccountVerificationModal } from '../../auth/components/PublisherAccountVerificationModal.js';
import { cleanTableOfContents } from '../../../shared/utils/tocHelper.js';
import { validateIsbn } from '../../../shared/utils/isbnValidator.js';

import { isAuthorSubmission, canDeleteBook } from './book-list/bookListUtils.js';
import { useBodyScrollLock } from '../../../shared/utils/scrollLock.js';

interface BookFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (book: Book) => void;
  initialBook?: Book | null;
  onDeleteRequested?: (id: string, title: string) => void;
  onOpenProfile?: () => void;
}

const getDefaultFormData = (user: any): BookFormData => ({
  title: '',
  subtitle: '',
  originalTitle: '',
  authorName: user?.role === 'penulis' ? user.name : '',
  authorWhatsapp: '',
  publisherName:
    (user?.role === 'penerbit' || user?.role === 'organisasi')
      ? (user?.organization || user?.publisherLegalName || user?.name || '')
      : 'Penerbit Mandiri',
  category: 'Fiksi & Sastra',
  publicationYear: new Date().getFullYear(),
  edition: '1',
  editionType: 'orisinal',
  editionNotes: '',
  pages: 0,
  language: 'Indonesia',
  isbnOriginal: '',
  synopsis: '',
  tableOfContents: '',
  coverUrl: '',
  purchaseUrl: '',
  status: 'diajukan',
});

export const BookFormModal: React.FC<BookFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  initialBook,
  onDeleteRequested,
  onOpenProfile,
}) => {
  const { user } = useAuthStore();
  const draftKey = user ? `uibn_book_form_draft_${user.id}` : 'uibn_book_form_draft';
  const [formData, setFormData] = useState<BookFormData>(() => getDefaultFormData(user));

  const { data: publishersData } = useQuery({
    queryKey: ['publishers'],
    queryFn: () => api.getPublishers(),
    enabled: isOpen && (user?.role === 'penulis' || user?.role === 'admin'),
  });

  const [previewUibn, setPreviewUibn] = useState<string>('');
  const [isGeneratingUibn, setIsGeneratingUibn] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCustomCategory, setIsCustomCategory] = useState(false);
  const [customCategory, setCustomCategory] = useState('');
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [isFlowGuideOpen, setIsFlowGuideOpen] = useState(false);
  const [isPublisherVerifyOpen, setIsPublisherVerifyOpen] = useState(false);

  const isPublisherUnverified = Boolean(
    (user?.role === 'penerbit' || user?.role === 'organisasi') &&
    !user?.isPublisherVerified &&
    user?.publisherVerificationStatus !== 'verified'
  );

  const isPublisherEditingAuthorBook = Boolean(
    initialBook && user?.role === 'penerbit' && isAuthorSubmission(initialBook, user)
  );

  const formContainerRef = React.useRef<HTMLFormElement>(null);

  // Load draft from localStorage or initialBook on mount/open
  useEffect(() => {
    if (!isOpen) return;

    if (initialBook) {
      const isPredefined = CATEGORIES.includes(initialBook.category);
      setIsCustomCategory(!isPredefined);
      setCustomCategory(!isPredefined ? initialBook.category : '');
      const currentPublisherName = (user?.role === 'penerbit' || user?.role === 'organisasi')
        ? (user?.organization || user?.publisherLegalName || user?.name || '')
        : initialBook.publisherName;

      setFormData({
        title: initialBook.title,
        subtitle: initialBook.subtitle || '',
        originalTitle: initialBook.originalTitle || '',
        authorName: initialBook.authorName,
        publisherName: currentPublisherName,
        category: isPredefined ? initialBook.category : 'Fiksi & Sastra',
        publicationYear: initialBook.publicationYear,
        edition: initialBook.edition || '1',
        editionType: (initialBook.editionType as EditionType) || 'orisinal',
        editionNotes: initialBook.editionNotes || '',
        pages: initialBook.pages || 0,
        language: initialBook.language || 'Indonesia',
        isbnOriginal: initialBook.isbnOriginal || '',
        synopsis: initialBook.synopsis || '',
        tableOfContents: cleanTableOfContents(initialBook.tableOfContents),
        coverUrl: initialBook.coverUrl || '',
        purchaseUrl: initialBook.purchaseUrl || '',
        authorWhatsapp: initialBook.authorWhatsapp || '',
        status: initialBook.status,
      });
      setPreviewUibn(initialBook.uibn);
      setDraftLoaded(false);
    } else {
      // Default new book state: start clean and blank
      setIsCustomCategory(false);
      setCustomCategory('');
      setFormData(getDefaultFormData(user));
      generateQuickPreview();
      setDraftLoaded(false);
    }
  }, [initialBook, user, isOpen, draftKey]);

  // Auto-save form progress to localStorage
  useEffect(() => {
    if (!initialBook && isOpen) {
      const hasContent =
        formData.title ||
        formData.subtitle ||
        formData.originalTitle ||
        (user?.role !== 'penulis' && formData.authorName) ||
        formData.isbnOriginal ||
        formData.synopsis ||
        formData.coverUrl ||
        formData.purchaseUrl;

      if (hasContent) {
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            formData,
            isCustomCategory,
            customCategory,
          })
        );
      }
    }
  }, [formData, isCustomCategory, customCategory, initialBook, isOpen, user, draftKey]);

  const generateQuickPreview = async (overridePubCode?: string) => {
    setIsGeneratingUibn(true);
    try {
      const pubCode = overridePubCode || formData.selectedPublisherCode || user?.publisherCode || (user?.role === 'organisasi' ? '8045' : user?.role === 'penerbit' ? '8042' : '9103');
      const res = await api.previewUibn(pubCode);
      setPreviewUibn(res.uibn);
    } catch {
      setPreviewUibn('UIBN-979-8000-1001-5');
    } finally {
      setIsGeneratingUibn(false);
    }
  };

  const handleResetDraft = () => {
    localStorage.removeItem(draftKey);
    setDraftLoaded(false);
    setIsCustomCategory(false);
    setCustomCategory('');
    setFormData(getDefaultFormData(user));
  };

  useBodyScrollLock(isOpen);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormErrors({});

    if (isPublisherEditingAuthorBook) {
      setFormErrors({
        form: 'Penerbit tidak dapat mengedit metadata naskah yang diajukan oleh Penulis secara langsung.',
      });
      return;
    }

    const submissionData = {
      ...formData,
      category: isCustomCategory ? customCategory.trim() : formData.category,
      tableOfContents: cleanTableOfContents(formData.tableOfContents),
      publisherName: (user?.role === 'penerbit' || user?.role === 'organisasi')
        ? (user?.organization || user?.publisherLegalName || user?.name || formData.publisherName)
        : formData.publisherName,
    };

    const customValidationErrors: Record<string, string> = {};

    if (!submissionData.title || !submissionData.title.trim()) {
      customValidationErrors.title = 'Judul buku wajib diisi (minimal 2 karakter).';
    } else if (submissionData.title.trim().length < 2) {
      customValidationErrors.title = 'Judul buku minimal terdiri dari 2 karakter.';
    }

    if (!submissionData.authorName || !submissionData.authorName.trim()) {
      customValidationErrors.authorName = 'Nama penulis buku wajib diisi.';
    } else if (submissionData.authorName.trim().length < 2) {
      customValidationErrors.authorName = 'Nama penulis minimal terdiri dari 2 karakter.';
    }

    if (!submissionData.publisherName || !submissionData.publisherName.trim()) {
      customValidationErrors.publisherName = 'Nama lembaga penerbit atau mandiri wajib diisi.';
    }

    if (!submissionData.category || !submissionData.category.trim()) {
      customValidationErrors.category = 'Pilih kategori atau genre buku.';
    }

    if (formData.selectedPublisherId && (!formData.authorWhatsapp || !formData.authorWhatsapp.trim())) {
      customValidationErrors.authorWhatsapp = 'Nomor WhatsApp aktif wajib diisi agar penerbit dapat menghubungi Anda.';
    }

    if (submissionData.isbnOriginal) {
      const isbnValidation = validateIsbn(submissionData.isbnOriginal);
      if (!isbnValidation.isValid) {
        customValidationErrors.isbnOriginal = isbnValidation.error || 'Format ISBN tidak valid.';
      }
    }

    if (submissionData.coverUrl && submissionData.coverUrl.startsWith('blob:')) {
      customValidationErrors.coverUrl = 'Gambar sampul sedang diproses atau gagal disinkronkan ke cloud. Silakan tunggu sebentar atau unggah ulang.';
    }

    if (Object.keys(customValidationErrors).length > 0) {
      setFormErrors(customValidationErrors);
      formContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const parseResult = formSchema.safeParse(submissionData);
    if (!parseResult.success) {
      const errors: Record<string, string> = {};
      parseResult.error.issues.forEach((issue) => {
        const key = issue.path[0] as string;
        errors[key] = issue.message;
      });
      setFormErrors(errors);
      formContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (isPublisherUnverified) {
      const entityLabel = user?.role === 'organisasi' ? 'Organisasi / Komunitas' : 'Lembaga Penerbit';
      setFormErrors({
        form: `${entityLabel} Belum Terverifikasi. Hanya ${entityLabel.toLowerCase()} yang telah diverifikasi resmi oleh UIBN yang dapat mendaftarkan buku baru. Silakan ajukan verifikasi terlebih dahulu.`,
      });
      setIsPublisherVerifyOpen(true);
      return;
    }

    setIsSubmitting(true);
    try {
      if (initialBook) {
        // Update
        const res = await api.updateBook(initialBook.id, submissionData);
        onSuccess(res.book);
      } else {
        // Create new with UIBN generation
        const pubCode = formData.selectedPublisherCode || user?.publisherCode || (user?.role === 'organisasi' ? '8045' : user?.role === 'penerbit' ? '8042' : '9103');
        const res = await api.createBook({
          ...submissionData,
          publisherCode: pubCode,
        });
        localStorage.removeItem(draftKey);
        onSuccess(res.book);
      }
      onClose();
    } catch (err: any) {
      setFormErrors({ form: err.message || 'Gagal menyimpan buku' });
      formContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/70 backdrop-blur-xs p-3 sm:p-4 lg:p-6 overflow-y-auto overscroll-contain animate-in fade-in duration-200"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        className="bg-white rounded-2xl max-w-2xl lg:max-w-4xl xl:max-w-5xl w-full max-h-[92vh] my-auto overflow-hidden shadow-2xl border border-slate-100 flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <BookFormHeader initialBook={initialBook} onClose={onClose} />

        {/* Form Body */}
        <form
          ref={formContainerRef}
          onSubmit={handleSubmit}
          noValidate
          className="overflow-y-auto p-5 sm:p-6 lg:p-8 space-y-5 flex-1"
        >
          {draftLoaded && <DraftRestoreBanner onResetDraft={handleResetDraft} />}

          {/* Validation Alert Summary */}
          {Object.keys(formErrors).length > 0 && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-900 text-xs flex items-start gap-2.5 animate-in fade-in duration-150">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
              <div className="space-y-1">
                <strong className="block font-bold">Harap periksa isian formulir:</strong>
                <p className="text-[11px] text-rose-700 leading-relaxed">
                  {formErrors.form || 'Beberapa bidang bertanda bintang (*) belum lengkap atau belum valid. Silakan periksa tanda merah di bawah.'}
                </p>
              </div>
            </div>
          )}

          {isPublisherEditingAuthorBook && (
            <div className="p-3.5 rounded-xl bg-purple-50 border border-purple-200 text-purple-950 flex items-start gap-2.5 text-xs">
              <AlertCircle className="w-4 h-4 text-purple-600 shrink-0 mt-0.5" />
              <div>
                <strong className="block text-purple-900 font-bold">Metadata Dikelola oleh Penulis</strong>
                <span className="text-purple-800 text-[11px] leading-relaxed">
                  Naskah ini diajukan langsung oleh Penulis ({initialBook?.authorName}). Penerbit tidak dapat mengedit metadata naskah secara langsung. Anda dapat menyetujui naskah untuk dikirim ke Admin UIBN atau menolaknya dengan catatan revisi agar diperbaiki oleh Penulis.
                </span>
              </div>
            </div>
          )}

          {isPublisherUnverified && (
            <div className="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-rose-950 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 text-xs">
              <div className="flex items-start gap-2.5">
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <div>
                  <strong className="block text-rose-900 font-bold">
                    Akun {user?.role === 'organisasi' ? 'Organisasi / Komunitas' : 'Penerbit'} Belum Terverifikasi
                  </strong>
                  <span className="text-slate-600 text-[11px]">
                    {user?.role === 'organisasi'
                      ? 'Organisasi/komunitas Anda belum diverifikasi oleh Admin UIBN. Silakan lengkapi verifikasi terlebih dahulu sebelum mendaftarkan buku.'
                      : 'Lembaga penerbit Anda belum diverifikasi oleh Admin UIBN. Silakan lengkapi verifikasi terlebih dahulu sebelum mendaftarkan buku.'}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setIsPublisherVerifyOpen(true)}
                className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white font-bold text-xs shrink-0 cursor-pointer shadow-2xs text-center"
              >
                Verifikasi Sekarang
              </button>
            </div>
          )}

          {formErrors.form && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{formErrors.form}</span>
            </div>
          )}

          {/* UIBN Code Generator Box */}
          <UibnPreviewCard
            initialBook={initialBook}
            previewUibn={previewUibn}
            isGeneratingUibn={isGeneratingUibn}
            onGenerateNew={generateQuickPreview}
          />

          {/* Form Input Fields */}
          <BookMetadataFields
            formData={formData}
            setFormData={setFormData}
            formErrors={formErrors}
            isCustomCategory={isCustomCategory}
            setIsCustomCategory={setIsCustomCategory}
            customCategory={customCategory}
            setCustomCategory={setCustomCategory}
            user={user}
            publishers={publishersData?.publishers || []}
            isApproved={Boolean(initialBook && initialBook.status === 'disetujui')}
            isTitleLocked={Boolean(initialBook && initialBook.status === 'disetujui')}
            onOpenFlowGuide={() => setIsFlowGuideOpen(true)}
            onOpenProfile={onOpenProfile}
          />

          {formErrors.form && (
            <div className="p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-800 text-xs flex items-center gap-2">
              <AlertCircle className="w-4 h-4 text-rose-600 shrink-0" />
              <span>{formErrors.form}</span>
            </div>
          )}

          {/* Footer & Submit Actions */}
          <BookFormFooter
            onClose={onClose}
            isSubmitting={isSubmitting}
            isEdit={Boolean(initialBook)}
            isDisabled={isPublisherEditingAuthorBook}
            onDelete={
              initialBook && canDeleteBook(initialBook, user) && onDeleteRequested
                ? () => {
                    onClose();
                    onDeleteRequested(initialBook.id, initialBook.title);
                  }
                : undefined
            }
          />
        </form>
      </div>

      <UibnSubmissionFlowGuideModal
        isOpen={isFlowGuideOpen}
        onClose={() => setIsFlowGuideOpen(false)}
        defaultPath={formData.selectedPublisherId ? 'penerbit' : 'mandiri'}
      />

      <PublisherAccountVerificationModal
        isOpen={isPublisherVerifyOpen}
        onClose={() => setIsPublisherVerifyOpen(false)}
      />
    </div>
  );
};
